import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useRef } from 'react';

import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
import { isNativeContainer } from 'utils/native-container';

/** Auth0's `AuthorizationParams` has no typed slot for this, so the same
 * literal is used to send it to loginWithRedirect and to read it off the URL
 * — kept as one constant rather than two copies that could drift apart. */
const SESSION_TRANSFER_TOKEN_PARAM = 'session_transfer_token';

/**
 * Captures the Auth0 Native-to-Web SSO session transfer token from the URL
 * and scrubs it immediately, at module-load time, before analytics, the
 * router or browser history can observe it (mobile app's own
 * src/services/sessionTransfer.ts appends it). Single-use and ~60s-lived.
 *
 * Residual risk: a URL-delivered token is still a login-CSRF / session-
 * fixation window — a victim opening an attacker's link within that short
 * life could sign into the attacker's account. Scrubbing immediately and
 * relying on single-use/short-life is the pragmatic mitigation; closing it
 * fully needs a native-generated nonce verified server-side (follow-up).
 */
function captureAndScrubSessionTransferToken(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get(SESSION_TRANSFER_TOKEN_PARAM);
    if (token === null) return null;

    params.delete(SESSION_TRANSFER_TOKEN_PARAM);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname +
        (query ? `?${query}` : '') +
        window.location.hash
    );
    return token;
  } catch {
    return null;
  }
}

const sessionTransferToken = captureAndScrubSessionTransferToken();

interface Props {
  children: JSX.Element;
}

/**
 * Redeems an Auth0 Native-to-Web SSO session transfer token through a single
 * explicit login, and blocks `children` (routes, their API calls, the axios
 * 401 interceptor) until that resolves — so the only thing that can call
 * Auth0 while a token is pending is this one explicit login, winning the
 * redemption race by construction and keeping the token off the provider's
 * default authorizationParams (so it never rides a silent/iframe/refresh
 * call). Only honours a token from inside the native container; a phishing
 * link opened in an ordinary browser is ignored.
 *
 * After loginWithRedirect returns, it's a fresh page load whose URL no
 * longer carries the token (scrubbed on first read), so this gate is
 * transparent from then on — including when redemption failed, which falls
 * through to the ordinary login flow rather than getting stuck.
 *
 * No-op in an ordinary browser (no token present).
 */
const SessionTransferGate = ({ children }: Props): JSX.Element => {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const loginRequestedRef = useRef(false);

  const hasNativeSessionTransferToken = !!sessionTransferToken && isNativeContainer();

  useEffect(() => {
    if (!hasNativeSessionTransferToken || isLoading || isAuthenticated || loginRequestedRef.current) {
      return;
    }
    loginRequestedRef.current = true;
    loginWithRedirect({
      authorizationParams: { [SESSION_TRANSFER_TOKEN_PARAM]: sessionTransferToken }
    });
  }, [hasNativeSessionTransferToken, isLoading, isAuthenticated, loginWithRedirect]);

  if (hasNativeSessionTransferToken && !isAuthenticated) {
    return <LoadingLogo />;
  }

  return children;
};

export default SessionTransferGate;
